// application/useFilterController.ts
import { useFilterStore } from './filterStore';
import { HomeType, MarketingStatus, Range, SortDirection, SortField } from '../domain/FilterTypes';

export function useFilterController() {
  const filter = useFilterStore((s) => s.filter);
  const setFilter = useFilterStore((s) => s.setFilter);
  const resetFilter = useFilterStore((s) => s.resetFilter);

  // ---- STATUS ----
  const marketingStatus = filter.marketingStatus;
  const setMarketingStatus = (status: MarketingStatus) => {
    setFilter(prev => ({ ...prev, marketingStatus: status }));
  };

  // ---- LOCATION ----
  const zipcode = filter.zipcode;
  const setZipcode = (zipcode: string | undefined) => {
    setFilter(prev => ({ ...prev, zipcode }));
  };

  const city = filter.city;
  const setCity = (city: string | undefined) => {
    setFilter(prev => ({ ...prev, city }));
  };

  // List Price
  const priceRange = filter.price;
  const setPriceRange = (min: number | null, max: number | null) => {
    setFilter(prev => ({
      ...prev,
      price: Range.create(min, max),
    }));
  };

  // ---- PRICE PER SQFT ----
  const pricePerSqftRange = filter.pricePerSqft;
  const setPricePerSqftRange = (min: number | null, max: number | null) => {
    setFilter(prev => ({
      ...prev,
      pricePerSqft: Range.create(min, max),
    }));
  };

  // ---- BEDS ----
  const bedsRange = filter.beds;
  const setBedsRange = (range: Range) => {
    setFilter(prev => ({
      ...prev,
      beds: range,
    }));
  };
  // ---- BATHS ----
  const bathsRange = filter.baths;
  const setBathsRange = (range: Range) => {
    setFilter(prev => ({
      ...prev,
      baths: range,
    }));
  };
  const clearBedsAndBaths = () => {
    setFilter(prev => ({
      ...prev,
      beds: Range.create(null, null),
      baths: Range.create(null, null),
    }));
  };

  // ---- HOME TYPES ----
  const homeTypes = filter.homeTypes;
  const setHomeTypes = (homeTypes: HomeType[]) => {
    setFilter(prev => ({
      ...prev,
      homeTypes,
    }));
  };

  // ---- TIME ON MARKET ----
  const timeOnMarketRange = filter.timeOnMarket;
  const setTimeOnMarketRange = (range: Range) => {
    setFilter(prev => ({
      ...prev,
      timeOnMarket: range,
    }));
  };

  // ---- SQFT ----
  const sqftRange = filter.sqft;
  const setSqftRange = (range: Range) => {
    setFilter((prev) => ({
      ...prev,
      sqft: range,
    }));
  };

  // ---- LOT SIZE ----
  const lotSizeRange = filter.lotSize;
  const setLotSizeRange = (range: Range) => {
    setFilter((prev) => ({
      ...prev,
      lotSize: range,
    }));
  };

  // ---- YEAR BUILT ----
  const yearBuiltRange = filter.yearBuilt;
  const setYearBuiltRange = (range: Range) => {
    setFilter((prev) => ({
      ...prev,
      yearBuilt: range,
    }));
  };

  // ---- STORIES ----
  const storiesRange = filter.stories;
  const setStoriesRange = (range: Range) => {
    setFilter((prev) => ({
      ...prev,
      stories: range,
    }));
  };

  // ---- PROPERTY TAXES ----
  const propertyTaxesRange = filter.propertyTaxes;
  const setPropertyTaxesRange = (range: Range) => {
    setFilter((prev) => ({
      ...prev,
      propertyTaxes: range,
    }));
  };

  // SORT
  const sortBy = filter.sortBy;
  const sortDirection = filter.sortDirection;
  const setSort = (newSortBy: SortField, newSortDirection: SortDirection) => {
    setFilter(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortDirection: newSortDirection,
    }));
  };

  // ESCAPE HATCH for anything else
  const setFilterDirect = (updater: (prev: any) => any) => {
    setFilter(updater);
  };

  return {
    filter,
    resetFilter,
    marketingStatus,
    setMarketingStatus,
    // LOCATION
    zipcode,
    setZipcode,
    city,
    setCity,
    // PRICE
    priceRange,
    setPriceRange,
    // PRICE PER SQFT
    pricePerSqftRange,
    setPricePerSqftRange,
    // BEDS
    bedsRange,
    setBedsRange,
    // BATHS
    bathsRange,
    setBathsRange,
    clearBedsAndBaths,
    // HOME TYPES
    homeTypes,
    setHomeTypes,
    // SORT
    sortBy,
    sortDirection,
    setSort,
    // TIME ON MARKET
    timeOnMarketRange,
    setTimeOnMarketRange,
    // SQFT
    sqftRange,
    setSqftRange,
    // LOT SIZE
    lotSizeRange,
    setLotSizeRange,
    // YEAR BUILT
    yearBuiltRange,
    setYearBuiltRange,
    // STORIES
    storiesRange,
    setStoriesRange,
    // PROPERTY TAXES
    propertyTaxesRange,
    setPropertyTaxesRange,
    // ESCAPE HATCH
    setFilterDirect,   // use this for simple cases
  };
}
